/**
 * Dashboard Type Definitions
 *
 * Comprehensive TypeScript interfaces for the attention-focused dashboard
 * TASK-164: Attention-Focused Dashboard UI with Mock Data
 */

// ============================================================================
// Alert Types
// ============================================================================

export type AlertLevel = 'critical' | 'high' | 'medium' | 'info';
export type AlertCategory = 'compliance' | 'portfolio' | 'document' | 'task' | 'client' | 'meeting' | 'transaction_monitoring' | 'media_monitoring';

export interface QuickAction {
  id: string;
  label: string;
  type: 'primary' | 'secondary' | 'danger';
  action: string; // Route or action identifier
}

export type AlertStatus = 'active' | 'read' | 'dismissed' | 'snoozed' | 'resolved';

/**
 * TASK-732: the server-owned action policy, projected onto every alert and Radar row.
 * The UI renders its actions FROM this contract and NEVER keeps its own
 * source-event → allowed-actions table — that table would drift from what the API
 * actually enforces, and the RM would be offered buttons that fail.
 */
export interface ActionPolicy {
  policy_id: string;
  version: number;
  mandatory: boolean;
  sla_class: 'urgent' | 'due_date' | 'high' | 'standard' | string;
  allowed_dispositions: string[];
  /** Per-disposition rationale rule. */
  rationale: Record<string, 'required' | 'optional'>;
  /** null = any authenticated user. Coarse system role names, not authorization detail. */
  allowed_roles: string[] | null;
  /**
   * TASK-784 (DEC-3): the disposition the SERVER proposes, or null where it proposes nothing.
   *
   * It is always a member of `allowed_dispositions` — enforced server-side at construction —
   * so a surface may render it as the primary affordance without checking. It must never be
   * rendered as the ONLY affordance: the owner's basis for DEC-3 is that the RM approves and
   * is therefore accountable, and an RM with one button has not approved anything.
   *
   * `null` is a real answer, distinct from an older payload that predates recommendations.
   */
  recommended_disposition?: string | null;
  /**
   * TASK-820: why a disposition is deliberately ABSENT, in the owner's words, from the server.
   * `key` is what a translator fills; `text` is the `t()` default so an unfilled locale
   * renders real words rather than a key. Empty = nothing authored, which renders as a plain
   * absence — never as an invented explanation.
   */
  absent_disposition_notes?: Array<{ disposition: string; key: string; text: string }>;
}

export interface DashboardAlert {
  id: string;
  level: AlertLevel;
  category: AlertCategory;
  title: string;
  description: string;
  aiInsight?: string;
  actionRequired: string;
  deadline?: string;
  relatedClientId?: string;
  relatedClientName?: string;
  quickActions?: QuickAction[];
  createdAt: string;
  /**
   * TASK-818: when an EMITTER last asserted this alert is still true — not when it was first
   * raised (`createdAt`, which never moves for a condition) and not when the row was last
   * written (`updatedAt`, which a human marking it read also moves). This is the field a
   * recency sort and an "as of" label must use; anything else reports the wrong thing.
   * Optional only for payloads from a backend older than migration 229 — fall back to
   * `createdAt`, never to "now".
   */
  lastSeenAt?: string;
  isRead?: boolean;
  isDismissed?: boolean;
  // TASK-381: Live alert system fields
  status?: AlertStatus;
  sourceService?: string;
  sourceEvent?: string;
  /**
   * TASK-820: what this source is CALLED, from the one server catalogue
   * (`shared/schemas/signal_source_labels`). `undefined`/`null` means the catalogue has no
   * entry — an honest unknown, never a fallback to the enum key.
   */
  actionLabel?: string | null;
  /** TASK-820: the coarse display grouping (drift | cio | dna | kyc | …). */
  sourceLabel?: string | null;
  relatedEntityType?: string;
  relatedEntityId?: string;
  snoozedUntil?: string;
  resolvedAt?: string;
  readAt?: string;
  metadata?: Record<string, unknown>;
  /** TASK-732: absent only on payloads from an older backend — treat as "no restriction". */
  actionPolicy?: ActionPolicy;
}

export interface AlertCounts {
  critical: number;
  high: number;
  medium: number;
  info: number;
  total: number;
  unread: number;
  /**
   * THE primary triage number (TASK-653), counted SERVER-SIDE over every unresolved row
   * (deep review #6). Optional because it is absent until `/api/alerts/counts` answers —
   * `primaryTriageCount()` falls back to the page-derived count until then.
   */
  unresolved_groups?: number;
}

// ============================================================================
// Priority Action Types
// ============================================================================

export type ActionType = 'meeting' | 'task' | 'document' | 'review' | 'call' | 'approval';
export type PreparationStatus = 'ready' | 'in_progress' | 'not_started';

export interface PriorityAction {
  id: string;
  type: ActionType;
  title: string;
  clientId: string;
  clientName: string;
  timeSlot?: string;
  startTime?: string;
  endTime?: string;
  estimatedDuration: string;
  preparationStatus?: PreparationStatus;
  importance: number; // 1-10 AI-calculated
  category: string;
  dueDate?: string;
  dueTime?: string;
  isOverdue?: boolean;
  notes?: string;
}

// ============================================================================
// Document Status Types
// ============================================================================

export type DocumentStatusType = 'ok' | 'warning' | 'critical' | 'expired';
export type DocumentCategory = 'kyc' | 'suitability' | 'transaction' | 'compliance' | 'advisory';

export interface DocumentClientStatus {
  id: string;
  name: string;
  status: DocumentStatusType;
  documentType: string;
  expiryDate?: string;
  daysUntilExpiry?: number;
  lastUpdated?: string;
}

export interface DocumentStatus {
  category: string;
  categoryType: DocumentCategory;
  total: number;
  upToDate: number;
  expiringWithin30Days: number;
  expired: number;
  clients: DocumentClientStatus[];
}

// ============================================================================
// Client Health Types
// ============================================================================

export type HealthTrend = 'improving' | 'stable' | 'declining';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface ClientAlert {
  id: string;
  type: 'risk' | 'opportunity' | 'compliance' | 'contact';
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  actionRequired?: string;
}

export interface ClientHealthCard {
  clientId: string;
  clientName: string;
  aum: number;
  healthScore: number; // 0-100
  trend: HealthTrend;
  trendValue?: number; // Numeric change in health score
  alerts: ClientAlert[];
  lastContact: string;
  nextMeeting?: string;
  aiSummary: string;
  riskLevel: RiskLevel;
  portfolioPerformance?: number;
  complianceStatus?: 'compliant' | 'pending' | 'overdue';
}

// ============================================================================
// Filter Types
// ============================================================================

export type TimeFrame = 'today' | 'week' | 'month' | 'all';

export interface DashboardFilters {
  timeframe: TimeFrame;
  priority: AlertLevel[];
  category: AlertCategory[];
  clientId?: string;
  showCompleted: boolean;
  showDismissed: boolean;
  riskLevel?: RiskLevel[];
  sortBy?: 'priority' | 'deadline' | 'client' | 'created';
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// Quick Stats Types
// ============================================================================

export interface QuickStats {
  totalClients: number;
  complianceRate: number;
  tasksDueToday: number;
  meetingsToday: number;
  urgentAlerts: number;
  portfolioHealthAvg: number;
}

// ============================================================================
// Dashboard State Types
// ============================================================================

export interface DashboardData {
  alerts: DashboardAlert[];
  priorityActions: PriorityAction[];
  documentStatus: DocumentStatus[];
  clientHealth: ClientHealthCard[];
  quickStats: QuickStats;
  expiringDocuments?: any[];
  overdueTasks?: any[];
  upcomingReviews?: any[];
  lastUpdated: string;
}

export interface DashboardState {
  data: DashboardData | null;
  filters: DashboardFilters;
  isLoading: boolean;
  error: string | null;
  expandedSections: {
    alerts: boolean;
    actions: boolean;
    documents: boolean;
    clients: boolean;
  };
}

// ============================================================================
// Color Configuration Types
// ============================================================================

export interface ColorScheme {
  bg: string;
  border: string;
  text: string;
  badge: string;
  icon: string;
}

export const PRIORITY_COLORS: Record<AlertLevel, ColorScheme> = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    badge: 'bg-red-100',
    icon: 'text-red-600',
  },
  high: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-800',
    badge: 'bg-orange-100',
    icon: 'text-orange-600',
  },
  medium: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    badge: 'bg-yellow-100',
    icon: 'text-yellow-600',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    badge: 'bg-blue-100',
    icon: 'text-blue-600',
  },
};

export const HEALTH_COLORS = {
  good: 'text-green-600',    // 80-100
  warning: 'text-yellow-600', // 50-79
  critical: 'text-red-600',   // 0-49
};

export const RISK_COLORS: Record<RiskLevel, string> = {
  low: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  critical: 'bg-red-100 text-red-800 border-red-200',
};

// ============================================================================
// Helper Functions
// ============================================================================

export const getHealthColor = (score: number): string => {
  if (score >= 80) return HEALTH_COLORS.good;
  if (score >= 50) return HEALTH_COLORS.warning;
  return HEALTH_COLORS.critical;
};

export const getHealthStatus = (score: number): 'good' | 'warning' | 'critical' => {
  if (score >= 80) return 'good';
  if (score >= 50) return 'warning';
  return 'critical';
};

export const getAlertIcon = (level: AlertLevel): string => {
  switch (level) {
    case 'critical': return '🔴';
    case 'high': return '🟠';
    case 'medium': return '🟡';
    case 'info': return '🔵';
    default: return '⚪';
  }
};

export const getTrendIcon = (trend: HealthTrend): string => {
  switch (trend) {
    case 'improving': return '↑↑';
    case 'stable': return '→';
    case 'declining': return '↓↓';
    default: return '→';
  }
};
